import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./JWTReceiver.scss";
import { fetchConfig } from "../../../common/util/config";
import { SetBootForCurrentBusiness, SetLoginFields, initAccBoot } from "../../../reducers/model/boot";
import type { BootUser } from "../../../reducers/boot";
import type { LoginFields } from "../../../reducers/loginLocalStorage";
import { redirectAuthFailed } from "../impersonation-util";
import { loadEntraAuthConfigs } from "../entra-config";
import type { EntraAuthState } from "../entra-util";
import { decodeEntraState, ENTRA_CODE_VERIFIER_KEY, ENTRA_STATE_NONCE_KEY } from "../entra-util";
import { checkWebsiteUnavailable, setHomePage, getLoginFields } from "../Token";
import {
  useLazyGetMyBizShellConfigQuery,
  useLazyGetBusinessSelectorQuery,
} from "../../../services/MyBizShell/mybizShellAPI";
import { persistor } from "../../../store";
import { sessionLog } from "../util";

type EntraTokenResponse = {
  token_type: string;
  expires_in: number;
  access_token: string;
  scope: string;
  id_token: string;
  initial_token: string;
  active: boolean;
  isImpersonation?: boolean;
  isoCountryCode: string;
  partyId: string;
};

/**
 * Entra ID (Azure AD) callback receiver, mounted at /entra-jwt-token.html.
 *
 * Reads the auth `code` + `state` from the redirect and the PKCE verifier stashed in
 * sessionStorage by EntraLogin, exchanges them via the /requestEntraTokens Lambda
 * (the browser sends no client secret), then boots the ABO business using the identity
 * carried in `state` — NOT from token claims (the Entra token is only proof of auth).
 * The tail (SetBootForCurrentBusiness -> initAccBoot -> setHomePage) mirrors the Okta
 * JWTReceiver.
 */
export const EntraReceiver = () => {
  const navigate = useNavigate();
  const [getShellConfig] = useLazyGetMyBizShellConfigQuery();
  const [getBusinessSelector] = useLazyGetBusinessSelectorQuery();
  const authInit = useRef(false);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const setProgress = (percent: string) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = percent;
    }
  };

  useEffect(() => {
    if (authInit.current) return;
    authInit.current = true;

    const handleEntraAuth = async () => {
      setProgress("20%");

      const localStorageFields: LoginFields = getLoginFields();
      SetLoginFields({ fields: localStorageFields });
      if (localStorageFields.ssoOrigin) {
        SetLoginFields({ fields: { ssoOrigin: localStorageFields.ssoOrigin } });
      }

      const defaultConfig = await fetchConfig();
      if (!defaultConfig?.env) {
        redirectAuthFailed(navigate);
        return;
      }
      checkWebsiteUnavailable(defaultConfig, navigate);
      setProgress("40%");

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const rawState = params.get("state");
      const verifier = window.sessionStorage.getItem(ENTRA_CODE_VERIFIER_KEY);

      if (!code || !rawState || !verifier) {
        sessionLog("Entra callback missing code, state, or PKCE verifier.");
        redirectAuthFailed(navigate);
        return;
      }

      let state: EntraAuthState;
      try {
        state = decodeEntraState(rawState);
        if (window.sessionStorage.getItem(ENTRA_STATE_NONCE_KEY) !== state.nonce) {
          throw new Error("Entra state nonce mismatch");
        }
      } catch {
        sessionLog("Failed to decode Entra state blob.");
        redirectAuthFailed(navigate);
        return;
      }

      const entra = loadEntraAuthConfigs(defaultConfig);

      // redirectUri MUST match the value sent to /authorize exactly (Entra enforces this).
      const local = window.location.href.startsWith("http://localhost");
      const localEntraReceiverUrl = "http://localhost:8000/entra-jwt-token.html";
      const redirectUri = local ? localEntraReceiverUrl : entra.RedirectUri;

      const tokenRequest = {
        code,
        clientId: entra.ClientId,
        redirectUri,
        tokenUrl: entra.TokenUrl,
        codeVerifier: verifier,
        aboNum: state.aboNumber,
        salesPlanAff: state.salesPlanAff,
      };

      try {
        const response = await fetch(`${entra.TokenApiUrlBase}/requestEntraTokens`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(tokenRequest),
        });
        if (!response.ok) {
          sessionLog(`requestEntraTokens failed with status ${response.status}.`);
          throw new Error(`requestEntraTokens returned ${response.status}`);
        }
        const tokenResponse: EntraTokenResponse = await response.json();
        if (!tokenResponse.access_token) {
          sessionLog("requestEntraTokens response is missing 'access_token'.");
          throw new Error("Missing access_token in Entra token response");
        }
        const isImpersonation = tokenResponse.isImpersonation === true;
        SetLoginFields({ fields: { isImpersonation } });

        if (!tokenResponse.active) {
          sessionLog("requestEntraTokens - ABO inactive");
          redirectAuthFailed(navigate);
          return;
        }

        setProgress("60%");

        // Build BootUser from the decoded state, not from token claims.
        const user: BootUser = {
          token_type: tokenResponse.token_type,
          access_token: tokenResponse.access_token,
          expires_in: tokenResponse.expires_in.toString(),
          idToken: tokenResponse.id_token,
          abo: state.aboNumber,
          aff: state.salesPlanAff,
          partyId: tokenResponse.partyId ?? "",
          globalPartyId: tokenResponse.partyId ?? "",
          isoCountryCode: tokenResponse.isoCountryCode || localStorageFields.ssoOrigin,
          cacheDateInMS: new Date().getTime(),
        };

        const updatedBoot = await SetBootForCurrentBusiness({ user, isImpersonation });
        await initAccBoot(updatedBoot);
        await persistor.flush();

        sessionLog("Successful Entra login!");
        await setHomePage(navigate, getShellConfig, getBusinessSelector);
      } catch {
        sessionLog("Entra authentication failed.");
        redirectAuthFailed(navigate);
      } finally {
        window.sessionStorage.removeItem(ENTRA_CODE_VERIFIER_KEY);
        window.sessionStorage.removeItem(ENTRA_STATE_NONCE_KEY);
      }
    };

    handleEntraAuth().catch(() => {
      sessionLog("Entra authentication failed.");
      redirectAuthFailed(navigate);
    });
  }, [getBusinessSelector, getShellConfig, navigate]);

  return (
    <div className="JwtImpersonation__progress">
      <div className="JwtImpersonation__bar" ref={progressBarRef}></div>
    </div>
  );
};
